import {
    Box,
    Chip,
    Typography,
    Grid,
    ChipProps,
    GridProps,
} from '@mui/material';
type ChipItem = {
    label: string;
    value: string;
};

type SttSelectableChipsProps = {
    grid?: boolean;
    name: string;
    caption?: string;
    items: ChipItem[];
    selected: string[];
    onChange: ({
        name,
        value,
    }: {
        name: string;
        value: string | string[];
    }) => void;
    ChipComponent?: typeof Chip;
    chipSize?: ChipProps['size'];
    variant?: 'outlined' | 'regular';
} & Omit<GridProps, 'onChange'>;

/**
 * SttSelectableChips: toggle one or many of a list of simple items. They are rendered as chips and accept a specific chip component
 */
export function SttSelectableChips({
    grid = false,
    name,
    caption,
    items,
    selected,
    onChange,
    ChipComponent = Chip,
    chipSize = 'medium',
    variant = 'regular',
    ...rest
}: SttSelectableChipsProps) {
    function select(value: string, isSelected: boolean) {
        if (isSelected) {
            onChange({ name, value: [...selected, value] });
        } else if (selected) {
            onChange({ name, value: selected.filter((v) => v !== value) });
        }
    }

    const content = (
        <Box position="relative">
            {caption && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: -10,
                        left: 12,
                        px: 1,
                        zIndex: 10,
                        borderRadius: 0.5,
                        backgroundColor: 'background.paper',
                    }}
                >
                    <Typography
                        sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                        }}
                    >
                        {caption}
                    </Typography>
                </Box>
            )}
            <Box
                sx={{
                    minHeight: 48,
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    p: 1.5,
                    ...(variant === 'outlined' && {
                        borderColor: 'divider',
                        borderRadius: 2,
                        borderStyle: 'solid',
                        borderWidth: 1,
                        backgroundColor: 'background.paper',
                        '&:hover': {
                            borderColor: 'primary.dark',
                        },
                        transition: 'border-color 0.2s ease-in-out',
                    }),
                }}
            >
                {items.map((item: ChipItem, i: number) => {
                    const isSelected =
                        selected.find((val: string) => val === item.value) !==
                        undefined;
                    return (
                        <ChipComponent
                            key={item.value || i}
                            size={chipSize}
                            label={item.label}
                            color={isSelected ? 'primary' : 'default'}
                            variant={isSelected ? 'filled' : 'outlined'}
                            onClick={() => select(item.value, !isSelected)}
                            sx={{
                                cursor: 'pointer',
                                fontWeight: isSelected ? 600 : 400,
                                transition: 'all 0.2s ease-in-out',
                                borderWidth: isSelected ? 2 : 1,
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                },
                                ...(isSelected && {
                                    boxShadow:
                                        '0 2px 6px rgba(186, 242, 0, 0.3)',
                                }),
                            }}
                        />
                    );
                })}
            </Box>
        </Box>
    );

    return grid ? <Grid {...rest}>{content}</Grid> : content;
}
